import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { invalidateReferenceTable } from './referenceDataApi';

// --- Types ---

/**
 * How the WinForms meal pattern form colours an allergy field
 * (CheckFoodAllergyAdjustment, PM_Utils Utils.vb:1872): `missing` is red (linked,
 * but no adjustment of the resident's covers it), `visualOnly` is yellow (linked
 * to no adjustment at all), `none` is no warning.
 */
export type FoodAllergyWarningKind = 'none' | 'missing' | 'visualOnly';

export interface FoodAllergyWarning {
  fKey_Food_Allergy: number;
  allergyName: string;
  fKey_Prd_Allergy_Group: number;
  allergyGroupName: string;
  /** True only when `warningKind` is `none`. */
  hasMatchingAdjustment: boolean;
  /** Absent from APIs older than the WinForms port; see `allergyWarningKind`. */
  warningKind?: FoodAllergyWarningKind;
}

/**
 * The warning kind, reading an older API (no `warningKind`) the safe way: any
 * allergy without a matching adjustment is `missing`.
 */
export function allergyWarningKind(w: FoodAllergyWarning): FoodAllergyWarningKind {
  if (w.warningKind === 'none' || w.warningKind === 'missing' || w.warningKind === 'visualOnly') return w.warningKind;
  return w.hasMatchingAdjustment ? 'none' : 'missing';
}

/**
 * Reference count behind the Delete guard — mirrors the C# DTO
 * `DietechModels/Models/FoodAllergyMenu.cs:FoodAllergyReferenceResult`.
 * Ports `FoodAllergyInUse` (ucDietech_Food_Allergies.vb:526-549), which
 * sums `DB_CheckOccurances` over ten resident slots and ten EHR-mapping
 * slots and refuses the delete when the total is non-zero.
 */
export interface FoodAllergyReferenceResult {
  patientCount: number;
  ehrMappingCount: number;
  /** patientCount + ehrMappingCount — the "{N} different areas" number. */
  totalCount: number;
}

/** Request body for Replace (ReplaceFoodAllergy, .vb:624-694). */
export interface FoodAllergyReplaceRequest {
  SourceFoodAllergyId: number;
  TargetFoodAllergyId: number;
}

export interface FoodAllergyReplaceResult {
  success: boolean;
  affectedPatients: number;
  affectedEhrMappings: number;
  /** Non-null on failure — surfaced in the error toast. */
  errorMessage: string | null;
}

// --- Query Keys ---

export const foodAllergyKeys = {
  all: ['foodAllergy'] as const,
  warnings: (facilityId: number, patientId: number) =>
    [...foodAllergyKeys.all, 'warnings', facilityId, patientId] as const,
  /** Under `warnings(...)`, so invalidating that prefix refreshes these too. */
  warningsFor: (facilityId: number, patientId: number, allergyIds: readonly number[]) =>
    [...foodAllergyKeys.warnings(facilityId, patientId), allergyIds.join(',')] as const,
  references: (facilityId: number, id: number) =>
    [...foodAllergyKeys.all, 'references', facilityId, id] as const,
};

// --- Queries ---

export function useFoodAllergyWarnings(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: foodAllergyKeys.warnings(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<FoodAllergyWarning[]>(
        API.foodAllergyWarnings(facilityId, patientId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load food allergy warnings', 'error'),
    },
  });
}

/**
 * The warnings for a given set of allergies (the resident page's slots, unsaved
 * edits included), so the page colours each slot by the same rule as the meal
 * pattern banner instead of guessing from the adjustment list. Keys are sorted
 * and de-duplicated; an empty set asks nothing. Keeps the previous answer while
 * a changed slot set loads, so the other slots don't blink.
 */
export function useFoodAllergyWarningsFor(facilityId: number, patientId: number, allergyIds: readonly number[]) {
  const ids = [...new Set(allergyIds.filter((id) => id > 0))].sort((a, b) => a - b);
  return useQuery({
    queryKey: foodAllergyKeys.warningsFor(facilityId, patientId, ids),
    queryFn: async () => {
      const { data } = await apiClient.get<FoodAllergyWarning[]>(
        API.foodAllergyWarnings(facilityId, patientId),
        { params: { allergyIds: ids.join(',') } },
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0 && ids.length > 0,
    placeholderData: keepPreviousData,
  });
}

// --- Menu actions (Tables → Food Allergies) ---

/**
 * How many places still reference this food allergy. WinForms
 * `FoodAllergyInUse` blocks the delete and shows "{Name} is currently in
 * use in {N} different areas." when this is non-zero, so the page asks
 * before deleting rather than orphaning twenty foreign-key columns.
 */
export async function fetchFoodAllergyReferences(
  facilityId: number,
  id: number,
): Promise<FoodAllergyReferenceResult> {
  const { data } = await apiClient.get<FoodAllergyReferenceResult>(
    API.foodAllergyReferences(facilityId, id),
  );
  return data ?? { patientCount: 0, ehrMappingCount: 0, totalCount: 0 };
}

/**
 * Merge one food allergy into another, then delete the source — the
 * Replace ribbon group on `ucDietech_Food_Allergies.vb`
 * (`beReplace` + `ReplaceFoodAllergy`). The source row is gone and the
 * target absorbed its references afterwards, so the table list, the
 * reference counts and the resident-facing allergy reads are all stale.
 */
export function useReplaceFoodAllergy(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation<FoodAllergyReplaceResult, Error, FoodAllergyReplaceRequest>({
    mutationFn: async (req) => {
      const { data } = await apiClient.post<FoodAllergyReplaceResult>(
        API.foodAllergyReplace(facilityId),
        req,
      );
      return data;
    },
    onSuccess: (result) => {
      if (!result?.success) return;
      queryClient.invalidateQueries({ queryKey: ['refData', 'foodAllergies'] }); void invalidateReferenceTable(queryClient, 'foodAllergies');
      queryClient.invalidateQueries({ queryKey: foodAllergyKeys.all });
      // WinForms clears the cached resident diet-fluctuation table after a
      // replace (`DT_ResidentDietFlucs(...) = Nothing`, .vb:688) because
      // every resident holding the old allergy now holds the new one.
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
  });
}
